import { useCallback, useEffect, useMemo, useState } from 'react';
import { SettingsFlow, UiNode } from '@ory/client';
import { ory } from 'src/auth/ory';
import { Logger } from 'src/utils/Logger';
import {
  isOryApiError,
  rewriteActionUrl,
  getCsrfTokenFromFlow,
  getOidcNodesByAction,
} from 'src/auth/utils/ory-helpers';

interface UseSettingsFlowReturn {
  flow: SettingsFlow | undefined;
  isLoading: boolean;
  error: string | null;
  oidcNodes: UiNode[];
  csrfToken: string;
  formAction: string;
  updatePassword: (password: string) => Promise<{ success: boolean }>;
  clearError: () => void;
  initialSuccess: boolean;
}

export const useSettingsFlow = (flowId: string): UseSettingsFlowReturn => {
  const [flow, setFlow] = useState<SettingsFlow>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialSuccess, setInitialSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const initFlow = async () => {
      try {
        const { data } = await ory.getSettingsFlow({ id: flowId });
        if (!cancelled) {
          setFlow(data);
          if (data.state === 'success') {
            setInitialSuccess(true);
          }
        }
      } catch (err: unknown) {
        if (cancelled) return;
        Logger.error('Failed to initialize settings flow', err);

        if (isOryApiError(err) && err.response?.status === 401) {
          setError('Your session has expired. Please log in again.');
        } else if (isOryApiError(err) && err.response?.status === 403) {
          setError('You need to re-authenticate to change your credentials.');
        } else {
          setError('Failed to initialize. Please try refreshing the page.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    initFlow();

    return () => {
      cancelled = true;
    };
  }, [flowId]);

  const oidcNodes = useMemo(() => getOidcNodesByAction(flow, ['link', 'unlink']), [flow]);

  const csrfToken = useMemo(() => getCsrfTokenFromFlow(flow), [flow]);

  const formAction = useMemo(() => rewriteActionUrl(flow?.ui.action ?? ''), [flow]);

  const updatePassword = useCallback(
    async (password: string): Promise<{ success: boolean }> => {
      if (!flow) {
        setError('Settings flow not initialized. Please refresh the page.');
        return { success: false };
      }

      setError(null);

      try {
        const { data } = await ory.updateSettingsFlow({
          flow: flow.id,
          updateSettingsFlowBody: {
            method: 'password',
            password,
            csrf_token: csrfToken,
          },
        });

        if (data.state === 'success') {
          return { success: true };
        }

        setFlow(data);
        const messages = data.ui.messages ?? [];
        const errorMessages = messages.filter(m => m.type === 'error');
        if (errorMessages.length > 0) {
          setError(errorMessages.map(m => m.text).join(' '));
        }
        return { success: false };
      } catch (err: unknown) {
        Logger.error('Failed to update password', err);

        if (!isOryApiError(err)) {
          setError('An unexpected error occurred. Please try again.');
          return { success: false };
        }

        const errorData = err.response?.data as SettingsFlow | undefined;
        if (errorData?.ui?.messages) {
          const messages = errorData.ui.messages;
          const errorMessages = messages?.filter((m: { type: string }) => m.type === 'error') ?? [];
          if (errorMessages.length > 0) {
            setError(errorMessages.map((m: { text: string }) => m.text).join(' '));
          } else {
            setError('Failed to update password. Please try again.');
          }
          setFlow(errorData);
        } else if (err.response?.status === 400) {
          setError(
            'The password does not meet the security requirements. Please try a different password.',
          );
        } else if (err.response?.status === 401) {
          setError('Your session has expired. Please log in again.');
        } else if (err.response?.status === 403) {
          setError('You need to re-authenticate to change your credentials.');
        } else {
          setError('An unexpected error occurred. Please try again.');
        }
        return { success: false };
      }
    },
    [flow, csrfToken],
  );

  const clearError = useCallback(() => setError(null), []);

  return {
    flow,
    isLoading,
    error,
    oidcNodes,
    csrfToken,
    formAction,
    updatePassword,
    clearError,
    initialSuccess,
  };
};
